import { type ReactNode } from 'react'

export type IRcsExampleKey =
  | 'useCaseDescription'
  | 'useCaseVideo'
  | 'messageFlow'
  | 'optOut'
  | 'sampleHelp'
  | 'sampleOptOut'

type IRcsExample = {
  title: string
  content: ReactNode
}

export const RCS_EXAMPLE_CONTENT: Record<IRcsExampleKey, IRcsExample> = {
  useCaseDescription: {
    title: 'Use case description example',
    content:
      'This campaign is used to send appointment reminders, order confirmations, and important account notifications to customers who have opted in.',
  },
  useCaseVideo: {
    title: 'Use case video example',
    content: (
      <>
        <b>
          The video should clearly demonstrate the end-to-end messaging flow and user experience,
          including:
        </b>
        <br />
        <br />
        <b>Opt-in Process:</b> Show a user interacting with your website, app, or another channel.
        The user enters their phone number and checks a box or gives explicit consent to receive
        messages. Display the confirmation message the user receives after opting in.
        <br />
        <br />
        <b>Triggering Events:</b> Illustrate the specific actions that cause messages to be sent
        (e.g., booking an appointment, placing an order, requesting information). Show the backend
        or admin interface, if relevant, where these triggers are set up.
        <br />
        <br />
        <b>Sample Messages:</b> Display examples of the types of messages the user receives
        (appointment reminders, order updates, notifications). Include a demonstration of a HELP
        message and a STOP message, showing how the user can get assistance or opt out.
        <br />
        <br />
        <b>User Interaction:</b> Show the user replying to messages (e.g., replying HELP or STOP).
        Demonstrate the system’s response to these keywords (e.g., sending a help message or
        confirming opt-out).
        <br />
        <br />
        <b>Compliance Elements:</b> Highlight the opt-in language, privacy policy, and any
        compliance requirements. If age-gated, show the age verification step.
        <br />
        <br />
        <b>Summary:</b> End with a brief summary of the use case, emphasizing how the campaign
        benefits the user and maintains compliance.
      </>
    ),
  },
  messageFlow: {
    title: 'Message flow and opt-in description example',
    content:
      'Customers receive messages after providing explicit consent by entering their mobile number and checking the “I agree to receive SMS updates” box during sign-up or checkout on our website or app. Once opted in, they receive a confirmation message. Subsequent messages are triggered by specific actions the customer takes, such as booking an appointment, placing an order, or requesting information. At any time, customers can reply HELP for assistance or STOP to opt out of receiving further messages.',
  },
  optOut: {
    title: 'Opt-out description example',
    content:
      'Customers can opt out of receiving messages at any time by replying with the keyword “STOP” to any message they receive. Once they send “STOP,” they will immediately be unsubscribed and will no longer receive further messages from our service. The opt-out process is clearly communicated in every initial message, and confirmation of successful opt-out is sent to the customer.',
  },
  sampleHelp: {
    title: 'Sample help message example',
    content:
      'For assistance, reply HELP or contact our support team at support@example.com. Msg&Data rates may apply.',
  },
  sampleOptOut: {
    title: 'Sample opt-out message example',
    content:
      'You have unsubscribed and will no longer receive messages from us. Reply START to resubscribe.',
  },
}
